import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  Row,
  Col,
  Empty,
  InputNumber,
  Form,
  Spin,
  Table
} from 'antd';
import {
  DownloadOutlined,
  LoadingOutlined,
  UnorderedListOutlined,
  WarningOutlined
} from '@ant-design/icons';
import useHvacFilterAccess from '@/features/hvacFilter/hooks/useHvacFilterAccess';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Layout from '@/common/Layout';
import CustomTable from '@/common/Content/CustomTable';
import CustomModal from '@/common/CustomModal';
import {
  createHvacLabel,
  getHvacLabels,
  getHvacLogs,
  regenerateMissingHvacPdfs
} from '@/features/hvacFilter/redux/slice';
import styles from './index.module.less';

const buildBlobPdfUrl = (rawUrl) => {
  if (!rawUrl) return '';
  const filename = String(rawUrl).split(/[?#]/)[0].split('/').pop();
  if (!filename) return '';
  return `${process.env.REACT_APP_STORAGE_URL}${filename}`;
};

const ACTION_LABELS_MAP = {
  label_created: 'Label Generated'
};

const HvacFilterGeneration = () => {
  const dispatch = useDispatch();
  const {
    list,
    listPagination,
    loading,
    pendingCount,
    stalePendingCount,
    hvacLogs,
    hvacLogsLoading
  } = useSelector((state) => state.hvacFilter);

  const savedPerPage = parseInt(localStorage.getItem('hvac_perPage'), 10) || 10;
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(savedPerPage);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // 2-step create modal state
  const [createStep, setCreateStep] = useState('count'); // 'count' | 'items'
  const [createOpen, setCreateOpen] = useState(false);
  const [countForm] = Form.useForm();
  const [itemRows, setItemRows] = useState([]); // [{ inhouse_number, location, error }]
  const [rowErrors, setRowErrors] = useState([]);

  const { canAct } = useHvacFilterAccess();

  // Activity log modal state
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logLabelNumber, setLogLabelNumber] = useState('');

  const pollRef = useRef(null);
  const fetchListRef = useRef(null);

  useEffect(() => {
    fetchList();
  }, [page, perPage, search]);

  useEffect(() => {
    if (pendingCount > 0) {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => fetchListRef.current(), 5000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current && pendingCount === 0) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [pendingCount]);

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    []
  );

  const fetchList = () => {
    const payload = { page, per_page: perPage };
    if (search) payload.search = search;
    dispatch(getHvacLabels(payload));
  };
  fetchListRef.current = fetchList;

  const openCreate = () => {
    countForm.setFieldsValue({ count: 1 });
    setCreateStep('count');
    setItemRows([]);
    setRowErrors([]);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    if (submitting) return;
    setCreateOpen(false);
    setCreateStep('count');
    setItemRows([]);
    setRowErrors([]);
    countForm.resetFields();
  };

  const handleCountNext = ({ count }) => {
    const n = Number(count) || 1;
    const rows = Array.from({ length: n }, () => ({
      inhouse_number: '',
      location: ''
    }));
    setItemRows(rows);
    setRowErrors(Array(n).fill(null));
    setCreateStep('items');
  };

  const updateRow = (index, field, value) => {
    setItemRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setRowErrors((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  const isRowValid = (row) =>
    row.inhouse_number.trim().length > 0 && row.location.trim().length > 0;

  const allRowsValid = itemRows.every(isRowValid);

  const handleCreate = async () => {
    if (submitting) return;

    // Validate all rows, mark errors
    const errors = itemRows.map((row) => {
      if (!row.inhouse_number.trim()) return 'In-house number is required';
      if (!row.location.trim()) return 'Location is required';
      return null;
    });
    setRowErrors(errors);

    if (errors.some(Boolean)) {
      return;
    }

    const items = itemRows.map((r) => ({
      inhouse_number: r.inhouse_number.trim(),
      location: r.location.trim()
    }));
    setSubmitting(true);
    try {
      const res = await dispatch(createHvacLabel({ items }));
      if (res?.payload?.success) {
        closeCreate();
        setPage(1);
        fetchList();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegenerateMissing = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      await dispatch(regenerateMissingHvacPdfs());
      fetchList();
    } finally {
      setRegenerating(false);
    }
  };

  const openLogModal = (hvacLabelNumber) => {
    setLogLabelNumber(hvacLabelNumber);
    setLogModalOpen(true);
    dispatch(getHvacLogs(hvacLabelNumber));
  };

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 70,
      render: (_, __, idx) => (page - 1) * perPage + idx + 1
    },
    {
      title: 'HVAC Label',
      dataIndex: 'hvac_label_number',
      key: 'hvac_label_number',
      render: (v) => <span style={{ fontFamily: 'monospace' }}>{v}</span>
    },
    {
      title: 'In-house Number',
      dataIndex: 'inhouse_number',
      key: 'inhouse_number',
      render: (v) => v || '—'
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (v) => v || '—'
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      render: (v) => v || '—'
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (v) =>
        v ? moment(v).utcOffset('+05:30').format('DD MMM YYYY HH:mm') : '—'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => {
        const isStale = new Date() - new Date(record.createdAt) > 90 * 1000;
        return (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {record.pdf_url ? (
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                href={buildBlobPdfUrl(record.pdf_url)}
                target="_blank"
                rel="noopener noreferrer"
                download={`${record.hvac_label_number}.pdf`}
                style={{ padding: 0 }}
              >
                PDF
              </Button>
            ) : isStale ? (
              <WarningOutlined
                style={{ color: '#faad14', cursor: 'pointer' }}
                title="PDF stalled — click to retry"
                onClick={handleRegenerateMissing}
              />
            ) : (
              <Spin size="small" />
            )}
            <Button
              type="link"
              size="small"
              icon={<UnorderedListOutlined />}
              onClick={() => openLogModal(record.hvac_label_number)}
              style={{ padding: 0 }}
            >
              Logs
            </Button>
          </div>
        );
      }
    }
  ];

  // Item table columns for the 2-step create modal
  const itemTableColumns = [
    {
      title: '#',
      key: 'idx',
      width: 50,
      render: (_, __, i) => i + 1
    },
    {
      title: 'In-house Number',
      key: 'inhouse_number',
      render: (_, row, i) => (
        <div>
          <Input
            value={row.inhouse_number}
            placeholder="e.g. HF-IH-001"
            onChange={(e) => updateRow(i, 'inhouse_number', e.target.value)}
            status={rowErrors[i] && !row.inhouse_number.trim() ? 'error' : ''}
          />
          {rowErrors[i] && !row.inhouse_number.trim() && (
            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 2 }}>
              {rowErrors[i]}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, row, i) => (
        <div>
          <Input
            value={row.location}
            placeholder="e.g. Block-A / Room 201 / AHU-3"
            onChange={(e) => updateRow(i, 'location', e.target.value)}
            status={rowErrors[i] && !row.location.trim() ? 'error' : ''}
          />
          {rowErrors[i] && !row.location.trim() && (
            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 2 }}>
              {rowErrors[i]}
            </div>
          )}
        </div>
      )
    }
  ];

  return (
    <Layout sider={false}>
      <div className={styles.page_wrapper}>
        <div className={styles.page_header}>
          <div className={styles.title_block}>
            <h2 className={styles.page_title}>HVAC Filter QR Generation</h2>
            {pendingCount > 0 && (
              <span
                className={
                  stalePendingCount > 0
                    ? styles.pending_chip_warn
                    : styles.pending_chip
                }
                title={
                  stalePendingCount > 0
                    ? 'One or more labels may have stalled'
                    : 'Labels are generating in the background'
                }
              >
                {stalePendingCount > 0 ? (
                  <WarningOutlined />
                ) : (
                  <LoadingOutlined spin />
                )}{' '}
                {pendingCount}{' '}
                {stalePendingCount > 0 ? 'may have stalled' : 'generating'}
                {stalePendingCount > 0 && (
                  <Button
                    type="link"
                    size="small"
                    onClick={handleRegenerateMissing}
                    loading={regenerating}
                    disabled={regenerating}
                    className={styles.pending_retry}
                  >
                    Retry
                  </Button>
                )}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              onClick={openCreate}
              disabled={submitting || !canAct}
            >
              Generate Labels
            </Button>
          </div>
        </div>

        <Row gutter={16} className={styles.search_row}>
          <Col xs={24} md={10} lg={8}>
            <Input.Search
              placeholder="Search by HVAC label or in-house number"
              allowClear
              onSearch={(v) => {
                setPage(1);
                setSearch(v);
              }}
            />
          </Col>
        </Row>

        <CustomTable
          columns={columns}
          data={list}
          rowKey={(r) => r.id}
          loading={loading}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty description="No HVAC filter labels found. Generate one to get started." />
            )
          }}
          pagination={{
            total: listPagination?.total_records || 0,
            pageSize: perPage,
            current: page,
            onChange: (p, ps) => {
              setPage(p);
              setPerPage(ps);
              localStorage.setItem('hvac_perPage', String(ps));
            }
          }}
          showPaginationMeta
        />

        {/* Step 1: Enter count */}
        <CustomModal
          title="Generate HVAC Filter Labels — Step 1 of 2"
          visible={createOpen && createStep === 'count'}
          onCancel={closeCreate}
          customFooter={false}
          footer={null}
          width={420}
        >
          <p style={{ color: '#595959', marginBottom: 16 }}>
            How many labels do you want to generate? You will specify the
            in-house number and location for each filter in the next step.
          </p>
          <Form
            form={countForm}
            layout="vertical"
            initialValues={{ count: 1 }}
            onFinish={handleCountNext}
          >
            <Form.Item
              label="Number of labels to generate"
              name="count"
              rules={[
                { required: true, message: 'Required' },
                {
                  type: 'number',
                  min: 1,
                  max: 100,
                  message: 'Enter a number between 1 and 100'
                }
              ]}
            >
              <InputNumber
                min={1}
                max={100}
                style={{ width: '100%' }}
                placeholder="e.g. 5"
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={closeCreate}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                style={{ marginLeft: 8 }}
              >
                Next →
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Step 2: Item details table */}
        <CustomModal
          title={`Generate HVAC Filter Labels — Step 2 of 2 (${
            itemRows.length
          } filter${itemRows.length !== 1 ? 's' : ''})`}
          visible={createOpen && createStep === 'items'}
          onCancel={closeCreate}
          customFooter={false}
          footer={null}
          width={680}
        >
          <p style={{ color: '#595959', marginBottom: 12 }}>
            Enter the in-house number and location for each HVAC filter. Both
            fields are required.
          </p>
          <Table
            columns={itemTableColumns}
            dataSource={itemRows}
            rowKey={(_, i) => i}
            pagination={false}
            size="small"
            scroll={{ y: 320 }}
          />
          <div
            style={{
              textAlign: 'right',
              marginTop: 16,
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <Button
              onClick={() => setCreateStep('count')}
              disabled={submitting}
            >
              ← Back
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={closeCreate} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleCreate}
                loading={submitting}
                disabled={submitting || !allRowsValid}
              >
                Generate {itemRows.length} Label
                {itemRows.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </CustomModal>

        {/* Activity Log modal */}
        <CustomModal
          title={`Activity Log — ${logLabelNumber}`}
          visible={logModalOpen}
          onCancel={() => setLogModalOpen(false)}
          customFooter={false}
          footer={null}
          width={600}
        >
          {hvacLogsLoading ? (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Spin />
            </div>
          ) : hvacLogs.length === 0 ? (
            <Empty description="No activity log entries found." />
          ) : (
            <Table
              dataSource={hvacLogs}
              rowKey={(r) => r.id}
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Action',
                  dataIndex: 'action',
                  key: 'action',
                  render: (v) => ACTION_LABELS_MAP[v] || v
                },
                {
                  title: 'Performed By',
                  dataIndex: 'performed_by_name',
                  key: 'performed_by_name',
                  render: (v) => v || '—'
                },
                {
                  title: 'Timestamp',
                  dataIndex: 'timestamp',
                  key: 'timestamp',
                  render: (v) =>
                    v
                      ? moment(v)
                          .utcOffset('+05:30')
                          .format('DD MMM YYYY HH:mm:ss') + ' IST'
                      : '—'
                }
              ]}
            />
          )}
        </CustomModal>
      </div>
    </Layout>
  );
};

export default HvacFilterGeneration;
